import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Movimentações e rastreabilidade (UC08, UC11, RF-076).
 *
 * O local é o par filial + depósito do Senior. É esta trilha que responde "em
 * que depósito está o produto e quem o moveu".
 */

const CAMINHO_COLECAO = montarCaminho('/movimentacoes');

export const tipoOperacaoSchema = z.enum(['Coleta', 'Movimentacao', 'ContagemInventario']);

/**
 * Dois desfechos de nascimento que a tela precisa distinguir: `Registrada`
 * (entre depósitos da mesma filial, aguarda a fila) e `Retida` (origem
 * indeterminada, aguarda decisão humana — UC08 4a). Movimentação no mesmo
 * depósito não existe mais: a API a recusa (`ORIGEM_IGUAL_AO_DESTINO`).
 */
export const statusOperacaoSchema = z.enum([
  'PendenteDispositivo',
  'Registrada',
  'Retida',
  'Enviando',
  'Consolidada',
  'Rejeitada',
  'Cancelada',
  // Lote que chegou depois de uma movimentação mais recente do mesmo lote:
  // só histórico, nunca vai ao ERP (wms-api, 2026-10-16).
  'Superada',
]);

export type TipoOperacao = z.infer<typeof tipoOperacaoSchema>;
export type StatusOperacao = z.infer<typeof statusOperacaoSchema>;

export const operadorDaLinhaSchema = z.object({
  id: z.string(),
  matricula: z.string(),
  nome: z.string(),
});

export const dispositivoDaLinhaSchema = z.object({
  id: z.string(),
  identificador: z.string(),
  apelido: z.string().nullable(),
});

export const movimentacaoSchema = z.object({
  id: z.string(),
  operacaoId: z.string(),
  sequencia: z.number().int(),
  tipo: tipoOperacaoSchema,
  status: statusOperacaoSchema,
  produto: z.string(),
  derivacao: z.string().nullable(),
  /**
   * Lote movido inteiro, lido no coletor. Opcional além de nulo: o painel sobe
   * antes da API que passou a mandar o campo, e a listagem não pode quebrar.
   */
  lote: z.string().nullable().optional(),
  /** Nulo = origem indeterminada (UC08 4a). */
  depositoOrigem: z.string().nullable(),
  filialOrigem: z.number().int().nullable(),
  /** Destino nunca é nulo (RF-021, RF-040). */
  depositoDestino: z.string(),
  filialDestino: z.number().int().nullable(),
  /**
   * Texto, não número: `Decimal(18,6)` do ERP não sobrevive ao ponto
   * flutuante do JavaScript, e a tela mostra a quantidade como veio.
   */
  quantidade: z.string(),
  /** UC08 4a — selo de origem indeterminada. */
  origemIndeterminada: z.boolean(),
  observacao: z.string().nullable(),
  /** Horário do aparelho. */
  registradoEm: z.string(),
  operador: operadorDaLinhaSchema.nullable(),
  dispositivo: dispositivoDaLinhaSchema.nullable(),
});

export type Movimentacao = z.infer<typeof movimentacaoSchema>;

const paginaDeMovimentacoesSchema = paginaSchema(movimentacaoSchema);

/**
 * Histórico de um produto (RF-076, UC11).
 *
 * `depositoAtual` e `trilha` têm padrão porque produto sem nenhuma
 * movimentação é resposta legítima (UC11 2a) — a tela mostra "sem histórico",
 * não um erro.
 */
export const historicoDeProdutoSchema = z.object({
  produto: z.string(),
  depositoAtual: z.string().nullable().default(null),
  filialAtual: z.number().int().nullable().default(null),
  atualizadoEm: z.string().nullable().default(null),
  total: z.number().int().nonnegative(),
  trilha: z.array(movimentacaoSchema).default([]),
});

export type HistoricoDeProduto = z.infer<typeof historicoDeProdutoSchema>;

export type FiltroDeMovimentacoes = ParametrosDePagina & {
  /** Início do período (ISO). */
  readonly de?: string;
  readonly ate?: string;
  /** Código exato. */
  readonly produto?: string;
  /** Encontra a movimentação pelos dois lados: depósito de origem ou de destino. */
  readonly deposito?: string;
  readonly codFil?: number;
  readonly operadorId?: string;
  readonly dispositivoId?: string;
  readonly origemIndeterminada?: boolean;
};

export async function listarMovimentacoes(
  filtro: FiltroDeMovimentacoes = {},
): Promise<Pagina<Movimentacao>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      de: filtro.de,
      ate: filtro.ate,
      produto: filtro.produto,
      deposito: filtro.deposito,
      codFil: filtro.codFil,
      operadorId: filtro.operadorId,
      dispositivoId: filtro.dispositivoId,
      origemIndeterminada: filtro.origemIndeterminada,
    },
  });

  return analisar(paginaDeMovimentacoesSchema, corpo, CAMINHO_COLECAO);
}

/** Depósito atual conhecido + trilha cronológica com autor e horário. */
export async function consultarHistoricoDoProduto(codigo: string): Promise<HistoricoDeProduto> {
  const caminho = montarCaminho('/produtos/{codigo}/historico', { codigo });
  return analisar(historicoDeProdutoSchema, await requisitar(caminho), caminho);
}
